import { CodeBrowser } from "components/icons/generated";
import BadgeNext from "ds/components/BadgeNext";
import Box from "ds/components/Box";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import LinkButton from "ds/components/Button/LinkButton";
import CardContent from "ds/components/Card/components/Content";
import CardHeader from "ds/components/Card/components/Header";
import CardWrapper from "ds/components/Card/components/Wrapper";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import IconEmphasized from "ds/components/IconEmphasized";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";
import { BlueprintVersionedGroup } from "types/generated";
import ListEntitiesItemDescriptionMultipleLines from "components/ListEntitiesItem/Description/MultipleLines";
import TagsList from "components/TagsList";
import { pluralize } from "shared/Pluralize";
import useCanCreateTemplateDeployment from "shared/Templates/access/useCanCreateTemplateDeployment";
import Tooltip from "ds/components/Tooltip";

import {
  getTemplateMarketplaceDeployFormUrl,
  getTemplateMarketplaceDeployFormAbsoluteUrl,
  getTemplateMarketplaceTemplateUrl,
  isTemplateNew,
} from "../utils";

type TemplateCardProps = {
  template: BlueprintVersionedGroup;
};

const TemplateCard = ({ template }: TemplateCardProps) => {
  const templateUrl = getTemplateMarketplaceTemplateUrl(template.id);
  const deploymentFormUrl = getTemplateMarketplaceDeployFormUrl(
    template.id,
    template.latestVersion!.id
  );
  const deploymentFormAbsoluteUrl = getTemplateMarketplaceDeployFormAbsoluteUrl(
    template.id,
    template.latestVersion!.id
  );

  const canDeployTemplate = useCanCreateTemplateDeployment();

  return (
    <CardWrapper variant="primary">
      <CardHeader
        prefix={<IconEmphasized icon={CodeBrowser} variant="secondary" />}
        suffix={
          <Box align="center" gap="medium">
            <Tooltip
              active={!canDeployTemplate.result}
              on={(props) => (
                <LinkButton
                  {...props}
                  variant="secondary"
                  size="medium"
                  to={deploymentFormUrl}
                  disabled={!canDeployTemplate.result}
                >
                  Deploy
                </LinkButton>
              )}
            >
              {canDeployTemplate.message}
            </Tooltip>

            {canDeployTemplate.result && (
              <DropdownMenuEllipsis tooltip="Template actions" dotsSize="medium">
                <CopyFieldDropdownMenuItem title="Copy deployment link" value={deploymentFormAbsoluteUrl} />
              </DropdownMenuEllipsis>
            )}
          </Box>
        }
      >
        {` `}
      </CardHeader>

      <CardContent hasHeader gap="large">
        <Box gap="large" align="center">
          <Link href={templateUrl} variant="secondary" size="inherit" noEllipsis>
            <Typography tag="span" variant="p-t7">
              {template.name}
            </Typography>
          </Link>
          <BadgeNext
            variant="gray"
            type="weak"
            text={`${template.deploymentsCount} ${pluralize("Deployment", template.deploymentsCount)}`}
          />
          {template.deploymentsCount === 0 && isTemplateNew(template.createdAt) && (
            <BadgeNext variant="blue" type="strong" text="NEW" />
          )}
        </Box>

        {template.description && (
          <Box direction="row" align="start">
            <ListEntitiesItemDescriptionMultipleLines
              lines={1}
              hasChevronLink
              description={template.description}
            />
          </Box>
        )}

        {template.labels.length > 0 && (
          <TagsList
            tags={template.labels}
            alwaysExpanded
            withEmptyPlaceholder
            withTopMargin={false}
          />
        )}
      </CardContent>
    </CardWrapper>
  );
};

export default TemplateCard;
